import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    customLogout?: () => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private httpClient = inject(HttpClient);
  private baseUrl = environment.baseApi;
  private authLocalStorageToken = `user`;
  private authDetailsLocalStorageToken = `userdetails`;

  // Add authentication state observer
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.isLoggedIn());
  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor() {
    // Initialize authentication state
    this.isAuthenticatedSubject.next(this.isLoggedIn());
  }

  login(data: any): Observable<any> {
    return this.httpClient.post(`${this.baseUrl}/auth/login`, data)
      .pipe(
        map((user) => {
          localStorage.setItem(this.authLocalStorageToken, JSON.stringify(user));
          this.isAuthenticatedSubject.next(true); // Update auth state
          const result = this.setAuthFromLocalStorage(user);
          return user;
        }),
        catchError((err) => {
          this.isAuthenticatedSubject.next(false); // Update auth state on error
          return of(err);
        }),
      );
  }

  logout() {
    localStorage.removeItem(this.authLocalStorageToken);
    this.isAuthenticatedSubject.next(false); // Update auth state

    // Call the custom logout function if it exists (for Flutter WebView)
    if (window.customLogout) {
      window.customLogout();
    }
  }

  isLoggedIn(): boolean {
    return localStorage.getItem(this.authLocalStorageToken) !== null;
  }

  forgotPassword(data: any) {
    return this.httpClient.post(`${this.baseUrl}/auth/forgot-password`, data)
      .pipe();
  }

  resetPassword(data: any) {
    return this.httpClient.post(`${this.baseUrl}/auth/forgot-password`, data)
      .pipe();
  }

  public setAuthFromLocalStorage(auth: any): boolean {
    if (auth) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      this.isAuthenticatedSubject.next(true); // Update auth state
      return true;
    }
    this.isAuthenticatedSubject.next(false); // Update auth state
    return false;
  }

  getAuthToken(): Promise<any> {
    const key = this.authLocalStorageToken;
    return new Promise((resolve, reject) => {
      try {
        const item = localStorage.getItem(key);
        if (item !== null) {
          resolve(JSON.parse(item));
        } else {
          reject('Item not found');
        }
      } catch (error) {
        reject('Error retrieving item');
      }
    });
  }

  getUser(key: string) {
    let a = localStorage.getItem(key);
    if (a !== null) {
      const userData = JSON.parse(a);
      return userData.user;
    }
    return '';
  }

  getToken() {
    let a = localStorage.getItem(this.authLocalStorageToken);
    if (a !== null) {
      const userData = JSON.parse(a);
      return userData.token;
    }
    return '';
  }

  // Add method to check authentication status and emit current state
  checkAuthStatus() {
    const isAuthenticated = this.isLoggedIn();
    this.isAuthenticatedSubject.next(isAuthenticated);
    return isAuthenticated;
  }
}
